// The MIDI preview's instrument — a subtractive synth, described as data so
// a lane's choice is one string ("pluck") and the engine builds the same
// voice on the live context and the offline one (lib/synth-voice.ts).
// Playback-only: Live plays its own instrument, and nothing here touches
// the wire.

import type { InstrumentId } from "@shared/instruments.js";

export interface SynthPatch {
  /** Preset id — what a MIDI lane's `instrument` names (shared/instruments.ts). */
  id: InstrumentId;
  label: string;
  /** "kit": a drum kit keyed by General MIDI pitch (lib/drums.ts) rather
   * than this subtractive voice; the fields below are then unused. */
  kind?: "kit";
  wave: OscillatorType;
  /** Seconds. */
  attack: number;
  decay: number;
  /** 0..1 of the peak. */
  sustain: number;
  release: number;
  /** Low-pass cutoff at middle C, Hz; Infinity ⇒ no filter. */
  cutoffHz: number;
  /** How the cutoff follows the note: it is `cutoffHz` at middle C and
   * scales by (pitch's Hz ÷ middle C's)^keyTrack — 0 fixed, 1 a constant
   * number of harmonics. Without it a fixed cutoff darkens (and quietens)
   * every note above the one it was tuned on. */
  keyTrack: number;
  /** Filter resonance (Q). */
  resonance: number;
  /** How far above `cutoffHz` the filter opens at the attack, as a multiple,
   * decaying back over `decay`. 1 ⇒ static. */
  filterEnv: number;
  /** How far apart the unison oscillators are, outermost to outermost, in
   * cents. 0 ⇒ in tune. */
  detuneCents: number;
  /** Output trim, so switching presets doesn't switch loudness. */
  level: number;
  /** Unison: how many copies of `wave`, spread over `detuneCents`. Absent ⇒
   * 1, or 2 when `detuneCents` asks for width. */
  voices?: number;
  /** How wide the unison copies sit, 0 (all centred) .. 1 (hard L/R). */
  spread?: number;
  /** A sine an octave down, at this level against the oscillators; it
   * skips the filter, so the low end holds while the cutoff moves. */
  sub?: number;
  /** White noise into the filter, at this level — breath, or a pick. */
  noise?: number;
  /** A sine at `ratio` × the note with its own short decay, after the
   * filter — an electric piano's tine, a mallet's strike. */
  partial?: { ratio: number; level: number; decaySec: number };
  /** Seconds the filter takes to fall from open back to its cutoff. Absent
   * ⇒ the amp's `decay`. */
  filterDecay?: number;
  /** 0..1: how much a soft note closes the filter's opening — 0 every
   * note as bright, 1 a velocity-0 note not opening at all. */
  velocityToFilter?: number;
  /** 0..1 of soft saturation after the filter: weight and harmonics. */
  drive?: number;
  /** Pitch wobble that fades in after `delaySec`. */
  vibrato?: { rateHz: number; cents: number; delaySec: number };
  /** 0..1 send to a small room shared by the lane's voices. */
  space?: number;
}

/** Where every preset sits, played the way its instrument usually is —
 * each `level` is calibrated to it by web-shared/test/sound-levels.spec.ts,
 * whose failure message names the level that would fix a miss. */
export const PRESET_LOUDNESS_LUFS = -20;

export const PRESETS: readonly SynthPatch[] = [
  // A pure tone — the one preset with nothing added.
  { id: "sine", label: "sine", wave: "sine", attack: 0.005, decay: 0.05, sustain: 1, release: 0.03, cutoffHz: Infinity, resonance: 0, keyTrack: 0, filterEnv: 1, detuneCents: 0, level: 0.757 },
  // Electric piano: a soft triangle body, a tine two octaves up that rings
  // off fast, harder notes brighter and a touch driven.
  {
    id: "keys", label: "keys", wave: "triangle", attack: 0.003, decay: 1.4, sustain: 0.35, release: 0.25,
    cutoffHz: 2400, resonance: 0.5, keyTrack: 0.5, filterEnv: 2.5, filterDecay: 0.6, velocityToFilter: 0.7, detuneCents: 0,
    partial: { ratio: 4, level: 0.3, decaySec: 0.35 }, drive: 0.25, space: 0.25, level: 0.760,
  },
  // Three detuned saws, a fast filter snap that velocity opens, a pick of noise.
  {
    id: "pluck", label: "pluck", wave: "sawtooth", attack: 0.002, decay: 0.3, sustain: 0.1, release: 0.15,
    cutoffHz: 700, resonance: 3, keyTrack: 0.6, filterEnv: 8, filterDecay: 0.18, velocityToFilter: 0.6,
    voices: 3, detuneCents: 14, spread: 0.6, noise: 0.06, space: 0.3, level: 1.940,
  },
  // Square into a low-pass with a short bite, a sine sub under it, saturated.
  {
    id: "bass", label: "bass", wave: "square", attack: 0.003, decay: 0.25, sustain: 0.75, release: 0.06,
    cutoffHz: 1000, resonance: 2, keyTrack: 0.6, filterEnv: 3.5, filterDecay: 0.12, velocityToFilter: 0.5, detuneCents: 0,
    sub: 0.5, drive: 0.45, level: 0.757,
  },
  // Four wide saws, a filter that breathes open slowly, air, slow vibrato, room.
  {
    id: "pad", label: "pad", wave: "sawtooth", attack: 0.45, decay: 1.2, sustain: 0.8, release: 0.9,
    cutoffHz: 1500, resonance: 0.6, keyTrack: 0.3, filterEnv: 1.8, filterDecay: 2, detuneCents: 22,
    voices: 4, spread: 1, noise: 0.04, vibrato: { rateHz: 4.2, cents: 5, delaySec: 0.4 }, space: 0.5, level: 0.937,
  },
  // Two squares just apart, vibrato once a note is held, some drive.
  {
    id: "lead", label: "lead", wave: "square", attack: 0.008, decay: 0.2, sustain: 0.85, release: 0.15,
    cutoffHz: 3200, resonance: 1.5, keyTrack: 0.5, filterEnv: 2, filterDecay: 0.15, velocityToFilter: 0.5,
    voices: 2, detuneCents: 8, spread: 0.3, vibrato: { rateHz: 5.5, cents: 14, delaySec: 0.3 }, drive: 0.3, space: 0.2, level: 0.649,
  },
  { id: "kit", label: "kit", kind: "kit", wave: "sine", attack: 0, decay: 0, sustain: 0, release: 0, cutoffHz: Infinity, resonance: 0, keyTrack: 0, filterEnv: 1, detuneCents: 0, level: 0.697 },
];

export const DEFAULT_PATCH: SynthPatch = PRESETS[0]!;

export function patchById(id: string | undefined): SynthPatch {
  return PRESETS.find((p) => p.id === id) ?? DEFAULT_PATCH;
}

/** One voice's amplitude schedule, fitted to the note. */
export interface VoiceEnvelope {
  /** Level at the top of the attack. */
  peak: number;
  /** Absolute times, seconds from the note's start. */
  attackEnd: number;
  decayEnd: number;
  /** Level held from decayEnd to the note's end. */
  sustainLevel: number;
  /** When the voice is silent — the oscillator's stop time. */
  end: number;
}

/**
 * Fit a patch's ADSR to one note. A note shorter than attack+decay still
 * rises and falls (the stages are squeezed proportionally), so a 32nd on a
 * pad is a blip, not silence; the release always runs past the note's end.
 * Velocity scales the peak; the constant keeps chords well under full scale.
 */
export function voiceEnvelope(patch: SynthPatch, durationSec: number, velocity: number): VoiceEnvelope {
  const d = Math.max(0.005, durationSec);
  const peak = Math.max(0, Math.min(1, velocity / 127)) * 0.2 * patch.level;
  const wanted = patch.attack + patch.decay;
  const squeeze = wanted > d ? d / wanted : 1;
  const attackEnd = patch.attack * squeeze;
  const decayEnd = attackEnd + patch.decay * squeeze;
  const sustainLevel = peak * Math.max(0, Math.min(1, patch.sustain));
  const release = Math.max(0.005, patch.release);
  return { peak, attackEnd, decayEnd, sustainLevel, end: d + release };
}

const MIDDLE_C_HZ = 440 * 2 ** (-9 / 12);

/** A patch's resting cutoff for one note, key tracking applied. */
export function trackedCutoff(patch: SynthPatch, frequencyHz: number): number {
  return patch.cutoffHz * (frequencyHz / MIDDLE_C_HZ) ** patch.keyTrack;
}
